import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Extract text from PDF file
export const extractTextFromPDF = async (file) => {
  try {
    console.log('📄 Starting PDF text extraction...');
    
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    
    let fullText = '';
    
    // Extract text from all pages
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }
    
    console.log('✅ PDF text extraction completed');
    return fullText;
    
  } catch (error) {
    console.error('❌ PDF text extraction failed:', error);
    throw new Error('Failed to extract text from PDF');
  }
};

// Extract structured invoice data from PDF text using regex patterns
export const extractInvoiceData = async (pdfText) => {
  try {
    console.log('🔍 Starting regex-based data extraction...');
    
    const extracted = {
      invoiceNumber: '',
      date: '',
      supplier: '',
      supplierAddress: '',
      supplierEmail: '',
      supplierPhone: '',
      client: '',
      clientAddress: '',
      clientEmail: '',
      clientPhone: '',
      totalHT: 0,
      tva: 0,
      totalTTC: 0,
      products: []
    };
    
    // Extract invoice number
    const invoiceNumberMatch = pdfText.match(/(?:Facture|Invoice|Ref|Reference|N°|No)[\s#:]*([A-Z0-9-]+)/i);
    if (invoiceNumberMatch) {
      extracted.invoiceNumber = invoiceNumberMatch[1].trim();
    }
    
    // Extract date
    const dateMatch = pdfText.match(/(?:Date|Le)[\s:]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})/i);
    if (dateMatch) {
      const dateStr = dateMatch[1];
      // Convert to YYYY-MM-DD format
      const parts = dateStr.split(/[\/\-\.]/);
      if (parts.length === 3) {
        let day, month, year;
        if (parts[2].length === 4) {
          // DD/MM/YYYY or DD-MM-YYYY
          day = parts[0].padStart(2, '0');
          month = parts[1].padStart(2, '0');
          year = parts[2];
        } else {
          // DD/MM/YY or DD-MM-YY
          day = parts[0].padStart(2, '0');
          month = parts[1].padStart(2, '0');
          year = '20' + parts[2];
        }
        extracted.date = `${year}-${month}-${day}`;
      }
    }
    
    // Extract supplier info
    const supplierInfo = extractSupplierInfo(pdfText);
    Object.assign(extracted, supplierInfo);
    
    // Extract totals
    const totals = extractTotals(pdfText);
    Object.assign(extracted, totals);
    
    // Extract products
    const products = extractProducts(pdfText);
    extracted.products = products;
    
    console.log('✅ Regex extraction completed:', extracted);
    return extracted;
    
  } catch (error) {
    console.error('❌ Regex extraction failed:', error);
    throw error;
  }
};

// Extract supplier information
const extractSupplierInfo = (text) => {
  const supplier = {
    supplier: '',
    supplierAddress: '',
    supplierEmail: '',
    supplierPhone: ''
  };
  
  // Extract supplier name (look for "Entreprise" or company name patterns)
  const supplierMatch = text.match(/(?:Entreprise|Company|Société)[\s:]*([^\n\r]+)/i);
  if (supplierMatch) {
    supplier.supplier = supplierMatch[1].trim();
  }
  
  // Extract supplier address
  const addressMatch = text.match(/(?:Adresse|Address)[\s:]*([^\n\r]+)/i);
  if (addressMatch) {
    supplier.supplierAddress = addressMatch[1].trim();
  }
  
  // Extract supplier email
  const emailMatch = text.match(/(?:Email|E-mail)[\s:]*([^\s@]+@[^\s@]+\.[^\s@]+)/i);
  if (emailMatch) {
    supplier.supplierEmail = emailMatch[1].trim();
  }
  
  // Extract supplier phone
  const phoneMatch = text.match(/(?:Téléphone|Phone|Tel)[\s:]*([+\d\s\-\(\)]+)/i);
  if (phoneMatch) {
    supplier.supplierPhone = phoneMatch[1].trim();
  }
  
  return supplier;
};

// Extract totals (HT, TVA, TTC)
const extractTotals = (text) => {
  const totals = {
    totalHT: 0,
    tva: 0,
    totalTTC: 0
  };
  
  // Extract Total HT
  const totalHTMatch = text.match(/(?:Total HT|Total hors taxe|HT)[\s:]*(\d+[,\.]\d{2})[\s€]*/i);
  if (totalHTMatch) {
    totals.totalHT = parseFloat(totalHTMatch[1].replace(',', '.'));
  }
  
  // Extract Total TTC
  const totalTTCMatch = text.match(/(?:Total TTC|Total toutes taxes|TTC)[\s:]*(\d+[,\.]\d{2})[\s€]*/i);
  if (totalTTCMatch) {
    totals.totalTTC = parseFloat(totalTTCMatch[1].replace(',', '.'));
  }
  
  // Extract TVA
  const tvaMatch = text.match(/(?:TVA|Taxe)[\s:]*(\d+[,\.]\d{2})[\s€]*/i);
  if (tvaMatch) {
    totals.tva = parseFloat(tvaMatch[1].replace(',', '.'));
  } else if (totals.totalTTC > 0 && totals.totalHT > 0) {
    // Calculate TVA if not found but we have HT and TTC
    totals.tva = totals.totalTTC - totals.totalHT;
  }
  
  return totals;
};

// Extract products from invoice table
const extractProducts = (text) => {
  const products = [];
  
  // Look for table headers and extract products after them
  const tableStartMatch = text.match(/(?:Description|Produit|Item).*?(?:Qte|Quantité|Quantity).*?(?:Prix|Price).*?(?:Total|Montant)/i);
  
  if (tableStartMatch) {
    const tableStartIndex = tableStartMatch.index + tableStartMatch[0].length;
    const tableText = text.substring(tableStartIndex);
    
    // Split into lines and process each line
    const lines = tableText.split('\n').filter(line => line.trim().length > 0);
    
    for (const line of lines) {
      // Skip lines that look like totals or headers
      if (line.match(/(?:Total|TVA|TTC|HT|Montant)/i)) {
        break;
      }
      
      const product = parseProductLine(line);
      if (product && product.name && product.name.trim() !== '') {
        products.push(product);
      }
    }
  }
  
  return products;
};

// Parse a single product line
const parseProductLine = (line) => {
  console.log('Parsing product line:', line);
  
  // Extract all prices in the line (unit price and total)
  const priceMatches = [...line.matchAll(/(\d+[,\.]\d{2})[\s€]*/g)];
  
  let unitPrice = 0;
  let totalPrice = 0;
  
  if (priceMatches.length >= 2) {
    // If there are multiple prices, the last one is usually the total
    unitPrice = parseFloat(priceMatches[0][1].replace(',', '.'));
    totalPrice = parseFloat(priceMatches[priceMatches.length - 1][1].replace(',', '.'));
  } else if (priceMatches.length === 1) {
    // If only one price, it could be unit price or total
    unitPrice = parseFloat(priceMatches[0][1].replace(',', '.'));
    totalPrice = unitPrice;
  }

  // Extract quantity (look for numbers at the beginning)
  const quantityMatch = line.match(/^(\d+)/);
  const quantity = quantityMatch ? parseInt(quantityMatch[1]) : 1;

  // Extract product name and description
  let name = '';
  let description = '';
  
  // Remove prices from the line to get the product info
  let productInfo = line;
  priceMatches.forEach(match => {
    productInfo = productInfo.replace(match[0], '').trim();
  });
  
  // Remove quantity from the beginning
  if (quantityMatch) {
    productInfo = productInfo.substring(quantityMatch[0].length).trim();
  }

  // Clean up the product info
  productInfo = productInfo.replace(/\s+/g, ' ').trim();

  // For the specific format from your example, try to split intelligently
  // Look for patterns like: "Bouteille slslkslks lkslsljs lsjlsjks Service de développement de site web vitrine"
  const words = productInfo.split(' ');
  
  if (words.length > 4) {
    // Try to find where the description starts (look for common description words)
    const descriptionStarters = ['service', 'développement', 'site', 'web', 'vitrine', 'de', 'la', 'le', 'un', 'une'];
    let descriptionStartIndex = -1;
    
    for (let i = 0; i < words.length; i++) {
      if (descriptionStarters.some(starter => words[i].toLowerCase().includes(starter))) {
        descriptionStartIndex = i;
        break;
      }
    }
    
    if (descriptionStartIndex > 0) {
      name = words.slice(0, descriptionStartIndex).join(' ').trim();
      description = words.slice(descriptionStartIndex).join(' ').trim();
    } else {
      // If no clear description starter, take first 2-3 words as name
      name = words.slice(0, Math.min(3, words.length)).join(' ').trim();
      description = words.slice(3).join(' ').trim();
    }
  } else {
    // For shorter lines, take first part as name
    name = words.slice(0, Math.min(2, words.length)).join(' ').trim();
    description = words.slice(2).join(' ').trim();
  }

  // Clean up the name and description
  name = name.replace(/^\d+\s*/, '').trim();
  description = description.trim();
  
  if (!name) name = 'Produit';
  if (!description) description = '';

  // Calculate unit price if we have total and quantity
  if (totalPrice > 0 && quantity > 1 && unitPrice === 0) {
    unitPrice = totalPrice / quantity;
  } else if (unitPrice === 0 && totalPrice > 0) {
    unitPrice = totalPrice;
  }

  const result = {
    name: name,
    quantity: quantity,
    priceHT: unitPrice,
    tva: 20,
    description: description,
    remise: 0
  };
  
  console.log('Parsed product:', result);
  return result;
};